import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";


const HeatMap = () => {
  const { t } = useTranslation();
  const [isZoomed, setIsZoomed] = useState(true);
  const roundHistory = useSelector((state) => state.dartGame.roundHistory);

  const OUTER_RADIUS = 170;
  const TRIPLE_RADIUS = 107;
  const DOUBLE_RADIUS = OUTER_RADIUS;
  const OUTER_BULL_RADIUS = 16;
  const INNER_BULL_RADIUS = 6.35;

  const { fieldCounts, missCount } = useMemo(() => {
    const counts = {};
    let misses = 0;
    roundHistory.forEach((round) => {
      round.hits.forEach((hit) => {
        if (hit.label === "Out") {
          misses++;
        } else {
          const field = hit.label.replace(/\s+/g, "");
          counts[field] = (counts[field] || 0) + 1;
        }
      });
    });
    return { fieldCounts: counts, missCount: misses };
  }, [roundHistory]);

  const maxCount = useMemo(() => {
    return Math.max(1, ...Object.values(fieldCounts));
  }, [fieldCounts]);

  const getColorForHitCount = (count) => {
    if (count > 20) return { fill: "#DC2626", opacity: 0.7 };
    if (count > 10) return { fill: "#DC2626", opacity: 0.5 };
    if (count > 5) return { fill: "#DC2626", opacity: 0.3 };
    if (count > 1) return { fill: "#DC2626", opacity: 0.1 };
    return { fill: "#DC2626", opacity: 0.05 };
  };

  const generateSegmentPath = (startAngle, endAngle, innerRadius, outerRadius) => {
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = outerRadius * Math.cos(startRad);
    const y1 = outerRadius * Math.sin(startRad);
    const x2 = outerRadius * Math.cos(endRad);
    const y2 = outerRadius * Math.sin(endRad);
    const x3 = innerRadius * Math.cos(endRad);
    const y3 = innerRadius * Math.sin(endRad);
    const x4 = innerRadius * Math.cos(startRad);
    const y4 = innerRadius * Math.sin(startRad);

    return `
      M ${x1} ${y1}
      A ${outerRadius} ${outerRadius} 0 0 1 ${x2} ${y2}
      L ${x3} ${y3}
      A ${innerRadius} ${innerRadius} 0 0 0 ${x4} ${y4}
      Z
    `;
  };

  const boardSegments = useMemo(() => {
    const segments = [];
    const numbers = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];

    numbers.forEach((number, i) => {
      const startAngle = i * 18 - 99;
      const endAngle = startAngle + 18;

      segments.push({ path: generateSegmentPath(startAngle, endAngle, DOUBLE_RADIUS - 8, OUTER_RADIUS), field: `D${number}` });
      segments.push({ path: generateSegmentPath(startAngle, endAngle, TRIPLE_RADIUS, DOUBLE_RADIUS - 8), field: `S${number}` });
      segments.push({ path: generateSegmentPath(startAngle, endAngle, TRIPLE_RADIUS - 8, TRIPLE_RADIUS), field: `T${number}` });
      segments.push({ path: generateSegmentPath(startAngle, endAngle, OUTER_BULL_RADIUS, TRIPLE_RADIUS - 8), field: `LS${number}` });
    });

    return segments;
  }, []);

  const radialLines = useMemo(() => {
    const lines = [];
    for (let i = 0; i < 20; i++) {
      const angle = ((i * 18 - 99) * Math.PI) / 180;
      const x = OUTER_RADIUS * Math.cos(angle);
      const y = OUTER_RADIUS * Math.sin(angle);
      const innerX = OUTER_BULL_RADIUS * Math.cos(angle);
      const innerY = OUTER_BULL_RADIUS * Math.sin(angle);
      lines.push(`M ${innerX} ${innerY} L ${x} ${y}`);
    }
    return lines;
  }, []);

  return (
    <div className="flex flex-col w-full">
      <div className="relative w-full aspect-square">
        <button
          onClick={() => setIsZoomed(!isZoomed)}
          className="absolute top-2 right-2 z-10 bg-secondary/90 hover:bg-primary/90 text-white p-2 rounded-full shadow-md transition-all duration-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
          title={isZoomed ? "Zoom Out" : "Zoom In"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {isZoomed ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM7 10h6m-3-3v6" />
            )}
          </svg>
        </button>

        <svg
          className="absolute inset-0 w-full h-full"
          viewBox={
            isZoomed
              ? `${-OUTER_RADIUS / 2} ${-OUTER_RADIUS * 1.01} ${OUTER_RADIUS} ${OUTER_RADIUS}`
              : `${-OUTER_RADIUS * 1.01} ${-OUTER_RADIUS * 1.01} ${OUTER_RADIUS * 2.03} ${OUTER_RADIUS * 2}`
          }
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient id="segmentGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.15)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0.15)" />
            </linearGradient>
          </defs>

          <circle r={OUTER_RADIUS} fill="#444444" stroke="#9CA3AF" strokeWidth="1" />
          <circle r={OUTER_RADIUS} fill="none" stroke="#9CA3AF" strokeWidth="0.75" opacity="0.6" />
          <circle r={DOUBLE_RADIUS - 8} fill="none" stroke="#9CA3AF" strokeWidth="0.75" opacity="0.6" />
          <circle r={TRIPLE_RADIUS} fill="none" stroke="#9CA3AF" strokeWidth="0.75" opacity="0.6" />
          <circle r={TRIPLE_RADIUS - 8} fill="none" stroke="#9CA3AF" strokeWidth="0.75" opacity="0.6" />

          {radialLines.map((line, index) => (
            <path key={`line-${index}`} d={line} stroke="#9CA3AF" strokeWidth="0.75" opacity="0.6" fill="none" />
          ))}

          {boardSegments.map((segment, index) => {
            const hitCount = fieldCounts[segment.field] || 0;

            return (
              <g key={`segment-${index}`}>
                <path d={segment.path} fill="#444444" stroke="#9CA3AF" strokeWidth="0.75" opacity="0.6" />
                {hitCount > 0 && (
                  <>
                    {(() => {
                      const color = getColorForHitCount(hitCount);
                      return (
                        color && (
                          <path
                            d={segment.path}
                            fill={color.fill}
                            opacity={color.opacity}
                            stroke="#9CA3AF"
                            strokeWidth="0.75"
                          />
                        )
                      );
                    })()}
                    <path d={segment.path} fill="url(#segmentGradient)" stroke="none" opacity="0.3" />
                  </>
                )}
              </g>
            );
          })}

          <circle r={OUTER_BULL_RADIUS} fill="#444444" stroke="#9CA3AF" strokeWidth="0.75" opacity="0.6" />
          {fieldCounts["S25"] && (
            <g>
              <circle r={OUTER_BULL_RADIUS} fill={getColorForHitCount(fieldCounts["S25"])} stroke="#9CA3AF" strokeWidth="0.75" opacity="0.6" />
              <circle r={OUTER_BULL_RADIUS} fill="url(#segmentGradient)" stroke="none" opacity="0.3" />
            </g>
          )}

          <circle r={INNER_BULL_RADIUS} fill="#444444" stroke="#9CA3AF" strokeWidth="0.75" opacity="0.6" />
          {fieldCounts["D25"] && (
            <g>
              <circle r={INNER_BULL_RADIUS} fill={getColorForHitCount(fieldCounts["D25"])} stroke="#9CA3AF" strokeWidth="0.75" opacity="0.6" />
              <circle r={INNER_BULL_RADIUS} fill="url(#segmentGradient)" stroke="none" opacity="0.3" />
            </g>
          )}
        </svg>
      </div>

      <div className="p-3 rounded-lg mt-2">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: "#DC2626", opacity: 0.7 }} />
            <span className="text-lightGray text-xs">{t("statistics.basicStats.heat20")}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: "#DC2626", opacity: 0.5 }} />
            <span className="text-lightGray text-xs">{t("statistics.basicStats.heat10")}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: "#DC2626", opacity: 0.3 }} />
            <span className="text-lightGray text-xs">{t("statistics.basicStats.heat5")}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: "#DC2626", opacity: 0.1 }} />
            <span className="text-lightGray text-xs">{t("statistics.basicStats.heat1")}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lightGray text-xs">
              | {t("statistics.basicStats.misses", { value: missCount })}
            </span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default HeatMap;